import { Emitter, Event } from "../../../base/common/events";
import { Disposable } from "../../../base/common/lifecycle";
import { CommandsRegistry, ICommandService } from "../../commands/common/commands";
import { ILogService } from "../../log/common/log";
import { AIToolCallResponse, AIToolServiceChangedEvent, IAITool, IAIToolService } from "./ai";
import { createToolFromCommand } from "./tools/commands.js";



export class AIToolService extends Disposable implements IAIToolService {


    constructor(
        @ILogService protected readonly logger: ILogService,
        @ICommandService protected readonly commands: ICommandService,
    ) {
        super();
        this._commandToolsNeedUpdate = true;
        this._register(this.commands.onDidChange(e => {
            this._commandToolsNeedUpdate = true;
        }));
    }

    private _commandToolsNeedUpdate = false;
    private readonly _registeredTools: IAITool[] = [];
    private readonly _commandTools: IAITool[] = [];




    private readonly _onDidChange = new Emitter<AIToolServiceChangedEvent>();
    readonly onDidChange = this._onDidChange.event;

    registerTool(tool: IAITool): void {
        if (!tool) {
            this.logger.error("Attempted to register null or undefined AI tool.");
            return;
        }
        else if (!tool.name) {
            this.logger.error("Attempted to register AI tool with no name.");
            return;
        }
        else if (this._registeredTools.find(t => t.name === tool.name)) {
            this.logger.warn(`AI tool with name '${tool.name}' is already registered. Skipping.`);
            return;
        }

        if (!this._registeredTools.includes(tool)) {
            this.logger.debug(`Registering AI tool '${tool.name}'.`);
            this._registeredTools.push(tool);
        }
    }

    async getAvailableTools(): Promise<IAITool[]> {
        this.updateToolsFromRegisteredCommandsIfNecessary();
        return [
            ...this._registeredTools,
            ...this._commandTools,
        ].filter(tool => {
            return tool.canRun ? tool.canRun() : true;
        })
    }

    callTool(toolName: string, parameters: object): Promise<AIToolCallResponse> {
        const tool = this.findToolByName(toolName);
        if (!tool) {
            return Promise.reject(new Error(`Tool '${toolName}' not found.`));
        }

        return tool.call(parameters)
            .catch(err => {
                this.logger.error(`Error calling tool '${toolName}':`, err);
                throw err;
            });;
    }

    private findToolByName(toolName: string): IAITool | null {
        const tool = this._registeredTools.find(t => t.name === toolName);
        if (tool) {
            return tool;
        }
        this.updateToolsFromRegisteredCommandsIfNecessary();
        const generatedTool = this._commandTools.find(t => t.name === toolName);
        if (generatedTool) {
            return generatedTool;
        }

        return null;
    }

    private updateToolsFromRegisteredCommandsIfNecessary() {
        if (this._commandToolsNeedUpdate) {
            this._commandToolsNeedUpdate = false;

            const commandList = CommandsRegistry.getCommands();

            const generatedCommands = new Array<string>();
            const skippedCommands = new Array<string>();

            for (const [key] of commandList) {
                if (this._commandTools.find(t => t.name === key)) {
                    // this.logger.debug(`AI tool for command '${key}' already exists. Skipping tool generation.`);
                    skippedCommands.push(key);
                    continue; // Tool for this command already exists
                }
                const tool = createToolFromCommand(key, this.commands, this.logger);
                if (tool) {
                    // this.logger.debug(`Generated AI tool for command '${key}':`, tool);
                    this._commandTools.push(tool);
                    generatedCommands.push(key);
                }
                else {
                    // this.logger.debug(`Command '${key}' is not suitable for AI tool generation. Skipping.`);
                    skippedCommands.push(key);
                }
            }

            this.logger.debug(`AI Tool Service: Generated tools for commands`, generatedCommands, `Skipped commands`, skippedCommands);

        }
    }


}